/*
 * The LINE OA Platform screen's state.
 *
 * Every action reloads the list. A connection's status is derived by the server from what is
 * configured, so patching a row locally would show a state the worker that actually sends does not
 * agree with — the same rule the organizer-side LINE screen follows.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminLineOaApi } from '../../../api';
import type { LineOaConnectionDto } from '../../../api';
import type { LineOaPlatformState } from './LineOaPlatformScreen';

export function useLineOaPlatform(token: string): LineOaPlatformState {
  const [connections, setConnections] = useState<LineOaConnectionDto[] | null>(null);
  const [environment, setEnvironment] = useState('');
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [list, env] = await Promise.all([
        superadminLineOaApi.list(token),
        superadminLineOaApi.environment(token).catch(() => ({ environment: '' })),
      ]);
      setConnections(list);
      setEnvironment(env.environment);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดรายการ LINE OA ไม่สำเร็จ');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (id: string, fn: () => Promise<unknown>, fallback: string) => {
    setBusy(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy('');
    }
  }, [load]);

  return {
    connections: connections ?? [],
    environment,
    loading: connections === null && !loadError,
    loadError, error, busy,
    verify: (id) => run(id, () => superadminLineOaApi.verify(token, id), 'ตรวจสอบการเชื่อมต่อไม่สำเร็จ'),
    setEnabled: (id, enabled) => run(id, () => superadminLineOaApi.setEnabled(token, id, enabled), 'เปลี่ยนสถานะไม่สำเร็จ'),
    rotate: (id, secrets) => run(id, () => superadminLineOaApi.rotateSecrets(token, id, secrets), 'บันทึก secret ไม่สำเร็จ'),
    reload: load,
  };
}
